import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { markDnd, DND_REASON_OPTIONS } from 'src/utils/dndApi';
import { showErrorToast, showSuccessToast } from 'src/utils/Toast/SnackbarProvider';

/**
 * Reusable "Add to DND" dialog. Shared by the companies table (T5), the dashboard
 * card / company detail page (T6), and anywhere else a company can be flagged DND.
 *
 * Props:
 *  - open:    boolean — controls visibility
 *  - onClose: () => void — called to dismiss the dialog
 *  - company: { id, company_name } — the company being flagged
 *  - onDone:  () => void — called after a successful mark (e.g. to refetch)
 */
export default function AddToDndDialog({ open, onClose, company, onDone }) {
  const [reasonCategory, setReasonCategory] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const resetState = () => {
    setReasonCategory('');
    setNote('');
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    resetState();
    onClose && onClose();
  };

  const handleConfirm = async () => {
    if (!reasonCategory || !company?.id) return;
    setSubmitting(true);
    try {
      const response = await markDnd(company.id, {
        reason_category: reasonCategory,
        note: note.trim() || null,
      });
      showSuccessToast(response?.data?.message || 'Company added to DND list');
      onDone && onDone();
      resetState();
      onClose && onClose();
    } catch (error) {
      const errKey =
        error?.response?.data && typeof error?.response?.data === 'object'
          ? Object.keys(error?.response?.data)?.[0]
          : null;
      const errMsg = error?.response?.data?.message || error?.response?.data?.[errKey];
      showErrorToast(error, errMsg || 'Failed to add company to DND list');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog maxWidth="xs" fullWidth open={open} onClose={handleClose}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="span">
            Add to DND
          </Typography>
          <IconButton size="small" onClick={handleClose} disabled={submitting}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {company?.company_name && (
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {company.company_name}
          </Typography>
        )}
        <FormControl fullWidth required sx={{ mb: 2 }}>
          <InputLabel id="dnd-reason-label">Reason</InputLabel>
          <Select
            labelId="dnd-reason-label"
            label="Reason"
            value={reasonCategory}
            onChange={(e) => setReasonCategory(e.target.value)}
          >
            {DND_REASON_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!reasonCategory || submitting}
        >
          {submitting ? 'Adding…' : 'Add to DND'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
